import React, { FC, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import QRCode from 'react-native-qrcode-svg'

import { getIsViewOnly } from '@ambire-common/utils/accounts'
import CloseIcon from '@common/assets/svg/CloseIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ShieldCheckFilledIcon from '@common/assets/svg/ShieldCheckFilledIcon'
import AccountAddress from '@common/components/AccountAddress'
import Alert from '@common/components/Alert'
import Avatar from '@common/components/Avatar/Avatar'
import DomainBadge from '@common/components/Avatar/DomainBadge'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import useController from '@common/hooks/useController'
import useNetworks from '@common/hooks/useNetworks'
import useReverseLookup from '@common/hooks/useReverseLookup'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useRailgunContext from '@web/hooks/useRailgunContext'

import getStyles from './styles'

type Props = {
  onClose: () => void
}

const ReceiveSheetContent: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()
  const { styles, theme } = useTheme(getStyles)

  const {
    state: { account }
  } = useController('SelectedAccountController')
  const {
    state: { keys }
  } = useController('KeystoreController')

  const { mode } = useAppMode()
  const isPrivate = mode === ModeEnum.PRIVATE
  const { currentRailgunWallet } = useRailgunContext()

  const accent = (isPrivate ? theme.privateAccent : theme.primary) as string

  const address = useMemo(() => {
    if (isPrivate && currentRailgunWallet?.railgunAddress) return currentRailgunWallet.railgunAddress
    return account?.addr || ''
  }, [isPrivate, currentRailgunWallet?.railgunAddress, account?.addr])

  const { isLoading: isDomainResolving, ens } = useReverseLookup({ address })
  const isViewOnly = useMemo(
    () => !account?.safeCreation && getIsViewOnly(keys, account?.associatedKeys || []),
    [account, keys]
  )

  const { label, pfp } = account?.preferences || { label: '', pfp: '' }

  const qrCodeRef: any = useRef(null)
  const [qrCodeError, setQrCodeError] = useState<string | boolean | null>(null)

  const allNetworks = useNetworks({ acc: account })
  const accountNetworks = useMemo(
    () =>
      allNetworks.filter((n) => {
        if (n.isNotSupported) return false
        // Private mode only supports the Railgun chains (Ethereum, Arbitrum, BNB)
        if (isPrivate) return EXTENSION_PRIVATE_CHAIN_IDS.includes(Number(n.chainId))
        return true
      }),
    [allNetworks, isPrivate]
  )

  return (
    <View style={spacings.phTy}>
      {/* Header — centered title + mode icon, close button top-right */}
      <View style={styles.header}>
        <View style={[flexbox.directionRow, flexbox.alignCenter]}>
          {isPrivate ? (
            <ShieldCheckFilledIcon width={20} height={20} color={accent} />
          ) : (
            <GlobeIcon width={20} height={20} color={accent} />
          )}
          <Text weight="medium" fontSize={18} style={spacings.plTy}>
            {isPrivate ? t('Private receive') : t('Public receive')}
          </Text>
        </View>
        <Pressable style={styles.closeButton} onPress={onClose} hitSlop={8}>
          <CloseIcon width={14} height={14} color={theme.secondaryText as string} />
        </Pressable>
      </View>

      {/* Account avatar + label */}
      <View style={[spacings.mtLg, flexbox.alignCenter]}>
        <Avatar
          size={48}
          pfp={pfp}
          address={account?.addr || ''}
          isSmart={!!account?.creation}
          style={spacings.pr0}
        />
        <Text weight="semiBold" fontSize={16} style={spacings.mtTy}>
          {label}
        </Text>
      </View>

      {/* QR code */}
      <View style={[spacings.mtLg, flexbox.alignCenter]}>
        {!!account && !qrCodeError && (
          <View style={styles.qrBox}>
            <QRCode
              value={address}
              size={180}
              quietZone={6}
              getRef={qrCodeRef}
              onError={() => setQrCodeError(t('Failed to load QR code!') as string)}
            />
          </View>
        )}
        {!!qrCodeError && (
          <Text appearance="errorText" weight="medium">
            {t('Failed to display QR code.')}
          </Text>
        )}
      </View>

      {/* Address pill with copy */}
      <View style={[spacings.mtLg, styles.addressPill]}>
        {!isPrivate && <DomainBadge ens={ens} />}
        <AccountAddress
          isLoading={isDomainResolving}
          ens={!isPrivate ? ens : undefined}
          address={address}
          plainAddressMaxLength={24}
          fontSize={14}
        />
      </View>

      {!!isViewOnly && (
        <View style={[spacings.mtMd, flexbox.alignCenter]}>
          <Alert size="sm" type="warning" title={t('Selected account is view only.')} />
        </View>
      )}

      {/* Supported networks */}
      {accountNetworks.length > 0 && (
        <View style={spacings.mtLg}>
          <Text
            weight="regular"
            appearance="tertiaryText"
            fontSize={13}
            style={[spacings.mbSm, { textAlign: 'center' }]}
          >
            {t('Supported networks')}
          </Text>
          <View style={styles.networks}>
            {accountNetworks.map(({ chainId, name }: any) => (
              <View key={chainId.toString()} style={styles.network}>
                <NetworkIcon
                  id={chainId.toString()}
                  size={32}
                  scale={1}
                  dataSet={createGlobalTooltipDataSet({
                    id: `receive-network-icon-${chainId.toString()}`,
                    content: name
                  })}
                />
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  )
}

export default React.memo(ReceiveSheetContent)
